import { OpenInNew } from '@mui/icons-material';
import { Alert, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import Block from '../../components/Block';
import Column from '../../components/Column';
import Heading from '../../components/Heading';
import Row from '../../components/Row';
import usePageTitle from '../../hooks/usePageTitle';
import usePrint, { PrintContext } from '../../hooks/usePrint';
import {
    HatDbContext,
    useHatDbContextFactory,
    useHatDbInitialCriteria,
    useHatDbStoreCriteria,
    useHatDbSyncSearchParams,
} from './context/HatDbContext';
import { HatDbDataContextProvider } from './context/HatDbDataContext';
import { Hat2DbContentSection } from './partials/dashboard/Hat2DbContentSection';
import { HatMediaType, mediaFilters } from './HatMetrics';
import { HatDbExportMenuButton } from './partials/export/HatDbExportMenuButton';
import Hat2CriteriaForm2 from './partials/forms/HatDbCriteriaForm';
import { HatDbDomainFilterToggle } from './partials/forms/HatDBDomainFilterToggle';
import HatDbMediaTabs from './partials/forms/HatDbMediaTabs';

export const attributionExplainTooltip =
    'Visits that can be directly attributed to our campaigns, and are reclassified from other channels.';

export function HatDashboardPage() {
    const navigate = useNavigate();
    const print = usePrint();

    // Initialize the dashboard context.
    const initialCriteria = useHatDbInitialCriteria();
    const hatDbContext = useHatDbContextFactory(initialCriteria);

    // Store some of the criteria in local storage.
    // Put some of the criteria on the search params of the URL.
    useHatDbStoreCriteria(hatDbContext.hatDbCriteria);
    useHatDbSyncSearchParams(hatDbContext.hatDbCriteria);

    // Update the page title to match the media filter.
    const mediaFilter = mediaFilters[hatDbContext.hatDbCriteria.mediaFilter];
    usePageTitle({ segments: ['HAT Reporting', mediaFilter.label] });

    // Only this needed to display a dashboard is the advertiser, everything else has defaults.
    const criteriaComplete = !!hatDbContext.hatDbCriteria.advertiserId;

    return (
        <PrintContext.Provider value={print}>
            <HatDbContext.Provider value={hatDbContext}>
                <HatDbDataContextProvider>
                    <Column gap={0} flex={1}>
                        <Block>
                            <Column gap={1}>
                                <Row spacing={2} justifyContent="space-between" alignItems="center">
                                    <Heading>HAT Reporting</Heading>

                                    <Row justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="medium"
                                            component={Link}
                                            to="https://app.trialfire.com"
                                            target="_blank"
                                            endIcon={<OpenInNew />}
                                        >
                                            Advanced Reporting
                                        </Button>
                                    </Row>
                                </Row>
                            </Column>
                        </Block>

                        <Block>
                            <Hat2CriteriaForm2
                                criteria={hatDbContext.hatDbCriteria}
                                onChangeCriteria={hatDbContext.setHatDbCriteria}
                            />
                        </Block>

                        <Block disableGutters>
                            <HatDbMediaTabs
                                mediaType={hatDbContext.hatDbCriteria.mediaFilter}
                                onChange={(mediaType: HatMediaType) => {
                                    hatDbContext.setHatDbCriteria({
                                        ...hatDbContext.hatDbCriteria,
                                        mediaFilter: mediaType,
                                    });

                                    // Update the route when a new media filter tab is selected.
                                    navigate(`/hat/${mediaType}`, { replace: false });
                                }}
                            />
                        </Block>

                        {criteriaComplete && (
                            <Block>
                                <Row flexDirection="row-reverse" justifyContent="space-between" pt={2}>
                                    <HatDbExportMenuButton />

                                    {hatDbContext.sharedToken && (
                                        <HatDbDomainFilterToggle
                                            advertiserDomain={hatDbContext.advertiserDomain}
                                            enabled={hatDbContext.hatDbCriteria.domainFilter}
                                            onChange={(enabled) => {
                                                hatDbContext.setHatDbCriteria({
                                                    ...hatDbContext.hatDbCriteria,
                                                    domainFilter: enabled,
                                                });
                                            }}
                                        />
                                    )}
                                </Row>
                            </Block>
                        )}

                        {criteriaComplete ? (
                            <Hat2DbContentSection />
                        ) : (
                            <Block>
                                <Alert severity="info" sx={{ my: 2 }}>
                                    Please select an advertiser.
                                </Alert>
                            </Block>
                        )}
                    </Column>
                </HatDbDataContextProvider>
            </HatDbContext.Provider>
        </PrintContext.Provider>
    );
}
